import {
  Button,
  Flex,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  type UseModalProps,
} from "@chakra-ui/react";
import { Trans, t } from "@lingui/macro";
import type { FinishedProjectDataFragment } from "@src/__generated__/graphql";
import {
  useFinishProjectMutation,
  useModalFinishProjectQuery,
} from "@src/__generated__/urql-graphql";
import { LoadingOverlay } from "@src/components/ui-kit";
import { onError } from "@src/utils/apollo";
import { can } from "@src/utils/components/permissions";
import { toApiDate } from "@src/utils/dates";
import cs from "classnames";
import { observer } from "mobx-react-lite";
import { useMemo, useState } from "react";
import { Banner } from "./Banner";
import type { ExpenseIncompletedProps } from "./Banner/ExpenseIncompleted";
import type { OurWorkIncompletedProps } from "./Banner/OurWorkIncompleted";
import FinishIllustration from "./FinishIllustration";
import FinishSuccess from "./FinishSuccess";
import s from "./ModalFinishProject.module.css";
import { FinishProjectSummary, SummaryContext } from "./models";

export interface ModalFinishProjectProps extends UseModalProps {
  /** Project.id */
  id: string;

  /** @isDirty is true when some of mutations was completed */
  onClosed?: (isDirty: boolean) => void;
  onCompleteOurWork?: OurWorkIncompletedProps["onCompleted"];
  onCompleteExpense?: ExpenseIncompletedProps["onCompleted"];
  onCreditRedistributed?: () => void;
  onFinished?: (project: FinishedProjectDataFragment) => void;
}

const ModalFinishProject = ({
  id,
  onClosed,
  onFinished,
  onCompleteOurWork,
  onCompleteExpense,
  onCreditRedistributed,
  ...modalProps
}: ModalFinishProjectProps) => {
  const [isDirty, setIsDirty] = useState(false);

  const onClose = () => {
    modalProps.onClose();
    onClosed?.(isDirty);
    setIsDirty(false);
  };

  const [{ data, fetching }, refetch] = useModalFinishProjectQuery({
    variables: { id },
    pause: !id || !modalProps.isOpen,
    ...onError(),
  });

  const summary = useMemo(() => {
    if (data?.getProjectCanBeFinishedSummary && data?.project) {
      return new FinishProjectSummary(
        data.getProjectCanBeFinishedSummary,
        !data.project.billable,
      );
    }
    return undefined;
  }, [data]);

  const [{ fetching: finishProjectLoading }, finishProject] =
    useFinishProjectMutation();

  const finishProjectAction = (
    <Button
      isLoading={finishProjectLoading}
      onClick={async () => {
        try {
          const { data } = await finishProject({ id });

          if (!data) return;

          const { finished_at } = data.finishProject;

          setIsDirty(false);
          summary?.finishProject();
          onFinished?.({
            ...data?.finishProject,
            finished_at: finished_at ? toApiDate(finished_at) : undefined,
          });
        } catch {}
      }}
    >
      <Trans>Yes, close the project</Trans>
    </Button>
  );

  return (
    <Modal
      isCentered
      isOpen={modalProps.isOpen}
      onClose={() => onClose()}
      scrollBehavior="inside"
      size="2xl"
      trapFocus={false}
    >
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>
          <Trans>Project closing</Trans>
        </ModalHeader>
        <ModalCloseButton />
        <ModalBody pos="relative">
          <LoadingOverlay
            isLoading={fetching}
            position="absolute"
            minH="100px"
            size="md"
          >
            {summary &&
              (summary.project_already_finished ? (
                <FinishSuccess />
              ) : (
                <div
                  className={cs(
                    s.finishProjectModal,
                    "flex flex-col items-center py-12 px-6",
                  )}
                >
                  <h1 className="font-bold text-4xl text-center mb-5">
                    {summary.can_be_finished_without_further_changes ? (
                      <Trans>Great! All conditions for closing are met.</Trans>
                    ) : (
                      <Trans>Some conditions for closing are not met.</Trans>
                    )}
                  </h1>
                  <p className="text-center">
                    {summary.can_be_finished_without_further_changes ? (
                      <Trans>Do you want close the project?</Trans>
                    ) : (
                      <Trans>
                        Please, resolve the issues below before closing the
                        project.
                      </Trans>
                    )}
                  </p>
                  <div className="py-10">
                    <FinishIllustration summary={summary} />
                  </div>

                  <SummaryContext.Provider value={summary}>
                    <Flex direction="column" gap="4" w="100%">
                      {summary.isOurWorkCompleted ? (
                        <Banner.OurWorkCompleted />
                      ) : (
                        <Banner.OurWorkIncompleted
                          items={summary.incomplete_our_work_budget_items}
                          onCompleted={(items) => {
                            setIsDirty(true);
                            refetch();
                            onCompleteOurWork?.(items);
                          }}
                        />
                      )}

                      {summary.isExpenseCompleted ? (
                        <Banner.ExpenseCompleted />
                      ) : (
                        <Banner.ExpenseIncompleted
                          items={summary.incomplete_expense_budget_items}
                          onCompleted={(items) => {
                            setIsDirty(true);
                            refetch();
                            onCompleteExpense?.(items);
                          }}
                        />
                      )}

                      {!summary.isProBono &&
                        (summary.isCreditCompleted ? (
                          <Banner.Invoiced />
                        ) : (
                          <Banner.Uninvoiced
                            summary={summary}
                            closeModal={onClose}
                            onCompleted={() => {
                              setIsDirty(true);
                              refetch();
                              onCreditRedistributed?.();
                            }}
                          />
                        ))}
                    </Flex>
                  </SummaryContext.Provider>
                </div>
              ))}
          </LoadingOverlay>
        </ModalBody>
        <ModalFooter gap="2">
          {summary?.project_already_finished === false && (
            <Button onClick={() => onClose()} variant="outline">
              {summary?.can_be_finished_without_further_changes
                ? t`No`
                : t`Close`}
            </Button>
          )}
          {(summary?.project_already_finished === true && (
            <Button onClick={() => onClose()}>
              <Trans>Sure thing!</Trans>
            </Button>
          )) ||
            (summary?.can_be_finished_without_further_changes === true &&
              finishProjectAction) ||
            (summary?.underinvoiced_for_project === false &&
              finishProjectAction) ||
            (summary?.underinvoiced_for_project === true &&
              can("credit_transfer_withDate") &&
              finishProjectAction) ||
            null}
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default observer(ModalFinishProject);
